import moviesImg from "../assets/images/categories/movies.webp";
import bowlingImg from "../assets/images/categories/bowling.webp";
import hikingImg from "../assets/images/categories/hiking.webp";
import museumImg from "../assets/images/categories/museum.webp";
import beachImg from "../assets/images/categories/beach.webp";
import gamingImg from "../assets/images/categories/gaming.webp";
import drinksImg from "../assets/images/categories/drinks.webp";
import concertImg from "../assets/images/categories/concert.webp";

// A kiki category drives a small, specialized creation form, but everything
// it collects funnels down to the same simple storage shape: a title (name),
// an optional location, and a free-text description/note (plus date/time/emoji).
export type KikiFieldKey = "title" | "location";

export type KikiField = {
  key: KikiFieldKey;
  label: string;
  placeholder: string;
};

export type KikiCategory = {
  id: string;
  label: string; // also the default title when a category has no title field
  emoji: string;
  gradient: string;
  image?: string; // optional background photo for the kiki card
  prompt: string; // heading shown on the per-category form
  fields: KikiField[];
  custom?: boolean; // lets the user pick the emoji + type their own title
};

export const KIKI_CATEGORIES: KikiCategory[] = [
  {
    id: "movies",
    label: "Movies",
    emoji: "🎬",
    gradient: "from-indigo-500 via-violet-600 to-purple-700",
    image: moviesImg,
    prompt: "Movie night",
    fields: [
      { key: "title", label: "Movie", placeholder: "What are you seeing?" },
      { key: "location", label: "Theater", placeholder: "Which theater?" },
    ],
  },
  {
    id: "bowling",
    label: "Bowling",
    emoji: "🎳",
    gradient: "from-emerald-400 via-teal-500 to-cyan-600",
    image: bowlingImg,
    prompt: "Let's bowl",
    fields: [{ key: "location", label: "Spot", placeholder: "Which alley?" }],
  },
  {
    id: "hiking",
    label: "Hiking",
    emoji: "🥾",
    gradient: "from-amber-500 via-orange-500 to-red-600",
    image: hikingImg,
    prompt: "Hit the trail",
    fields: [{ key: "location", label: "Trail", placeholder: "Which trail or where?" }],
  },
  {
    id: "museum",
    label: "Museum",
    emoji: "🎨",
    gradient: "from-purple-500 via-fuchsia-500 to-pink-500",
    image: museumImg,
    prompt: "Museum trip",
    fields: [{ key: "location", label: "Museum", placeholder: "Which museum?" }],
  },
  {
    id: "beach",
    label: "Beach day",
    emoji: "🏖️",
    gradient: "from-sky-400 via-cyan-400 to-teal-500",
    image: beachImg,
    prompt: "Beach day",
    fields: [{ key: "location", label: "Beach", placeholder: "Which beach?" }],
  },
  {
    id: "gaming",
    label: "Gaming",
    emoji: "🎮",
    gradient: "from-violet-600 via-fuchsia-600 to-pink-600",
    image: gamingImg,
    prompt: "Game session",
    fields: [
      { key: "title", label: "Game", placeholder: "What are you playing?" },
      { key: "location", label: "Where", placeholder: "Online or where?" },
    ],
  },
  {
    id: "drinks",
    label: "Drinks",
    emoji: "🍸",
    gradient: "from-rose-500 via-pink-500 to-fuchsia-500",
    image: drinksImg,
    prompt: "Grab drinks",
    fields: [{ key: "location", label: "Spot", placeholder: "Which bar?" }],
  },
  {
    id: "concert",
    label: "Concert",
    emoji: "🎵",
    gradient: "from-blue-600 via-indigo-600 to-violet-700",
    image: concertImg,
    prompt: "Live show",
    fields: [
      { key: "title", label: "Show", placeholder: "Who's playing?" },
      { key: "location", label: "Venue", placeholder: "Which venue?" },
    ],
  },
  {
    id: "other",
    label: "Other",
    emoji: "✨",
    gradient: "from-primary-400 to-violet-500",
    prompt: "Make your own",
    custom: true,
    fields: [
      { key: "title", label: "Activity", placeholder: "What do you want to do?" },
      { key: "location", label: "Where", placeholder: "Where? (optional)" },
    ],
  },
];

// Emoji choices offered for custom ("Other") kikis.
export const KIKI_CUSTOM_EMOJIS = ["✨", "🎉", "🍳", "🛍️", "🏋️", "🎤", "🎲", "📚", "🚲", "🐶", "🌮", "🍷"];

export const getKikiCategory = (id: string | null | undefined): KikiCategory | undefined =>
  id ? KIKI_CATEGORIES.find((c) => c.id === id) : undefined;

// Fallback gradient for a kiki whose emoji doesn't map to a preset category
// (e.g. a custom-emoji kiki). Shared so the carousel card and the kiki detail
// page render the same poster.
export const DEFAULT_KIKI_GRADIENT = "from-primary-500 via-primary-600 to-violet-700";

export const getKikiCategoryByEmoji = (emoji: string | null | undefined): KikiCategory | undefined =>
  emoji ? KIKI_CATEGORIES.find((c) => c.emoji === emoji) : undefined;
